import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useAuth } from '../../context/AuthContext';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

function fmtWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 60000) return 'Just now';
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
    if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
    return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

export default function PortalNotificationsBell() {
  const navigate = useNavigate();
  const { token, isManagerOrAdmin } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!token || !isManagerOrAdmin()) return;
    try {
      const res = await fetch(`${API}/portal-notifications?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count ?? 0);
      }
    } catch {
      /* silent — bell is non-critical */
    }
  }, [token, isManagerOrAdmin]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleOpen = (next) => {
    setOpen(next);
    if (next) fetchNotifications();
  };

  const markRead = async (id) => {
    try {
      await fetch(`${API}/portal-notifications/${id}/read`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      /* ignore */
    }
  };

  const openNotification = async (n) => {
    if (!n.read) await markRead(n.id);
    setOpen(false);
    if (n.href) navigate(n.href);
    else navigate('/portal/inbox');
  };

  if (!isManagerOrAdmin()) {
    return (
      <Button variant="ghost" size="icon" onClick={() => navigate('/portal/dashboard')} title="Dashboard">
        <Bell className="h-5 w-5 text-text-muted" />
      </Button>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={handleOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" title="Notifications">
          <Bell className="h-5 w-5 text-text-muted" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="px-3 py-2 border-b border-[#E4E8EB]">
          <p className="text-sm font-semibold text-text-primary">Notifications</p>
          <p className="text-xs text-text-muted">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </p>
        </div>
        {notifications.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-text-muted">
            No notifications yet. Replies to sales emails will appear here.
          </div>
        ) : (
          notifications.slice(0, 12).map((n) => (
            <DropdownMenuItem
              key={n.id}
              className={`flex flex-col items-start gap-0.5 py-2.5 cursor-pointer ${!n.read ? 'bg-primary/5' : ''}`}
              onClick={() => openNotification(n)}
            >
              <div className="flex items-center gap-2 w-full">
                <Mail className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                <span className="text-sm font-medium truncate flex-1">{n.title}</span>
                <span className="text-[10px] text-text-muted flex-shrink-0">{fmtWhen(n.created_at)}</span>
              </div>
              {n.body && (
                <p className="text-xs text-text-muted line-clamp-2 pl-5 w-full">{n.body}</p>
              )}
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => { setOpen(false); navigate('/portal/inbox'); }}>
          Open Portal Inbox
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}